import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react'
import { patchPurchasingPurchaseLine } from '@/services/purchasing'
import type {
  PurchasingPurchaseLinePatchRequest,
  PurchasingPurchaseLineReviewResponse,
  PurchasingReviewQueueRow,
} from '@/services/purchasingTypes'
import { buildReasonsForQueueRow } from './purchasingReviewTableConfig'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'

export const AUTOSAVE_DEBOUNCE_MS = 350

export type LineEditDraft = {
  vendorItemCode: string
  description: string
  packText: string
  quantity: string
  unitPrice: string
  amount: string
}

export function lineEditDraftFromRow(row: PurchasingReviewQueueRow): LineEditDraft {
  return {
    vendorItemCode: row.vendor_item_code ?? '',
    description: row.description ?? '',
    packText: row.pack_text ?? '',
    quantity: row.quantity ?? '',
    unitPrice: row.unit_price ?? '',
    amount: row.extended_price ?? '',
  }
}

export function buildPurchaseLinePatch(draft: LineEditDraft): PurchasingPurchaseLinePatchRequest {
  return {
    vendor_item_code: draft.vendorItemCode || undefined,
    description: draft.description || undefined,
    pack_text: draft.packText || undefined,
    quantity: draft.quantity || undefined,
    unit_price: draft.unitPrice || undefined,
    extended_price: draft.amount || undefined,
  }
}

export function lineEditDraftsEqual(a: LineEditDraft, b: LineEditDraft): boolean {
  return (
    a.vendorItemCode === b.vendorItemCode &&
    a.description === b.description &&
    a.packText === b.packText &&
    a.quantity === b.quantity &&
    a.unitPrice === b.unitPrice &&
    a.amount === b.amount
  )
}

export function lineEditDraftIsDirty(
  draft: LineEditDraft,
  row: PurchasingReviewQueueRow,
): boolean {
  return !lineEditDraftsEqual(draft, lineEditDraftFromRow(row))
}

export function computeCostPerAtomicForDraft(
  draft: LineEditDraft,
  atomicPerPack: string | null | undefined,
  fallback: string | null | undefined,
): string {
  const qty = Number(draft.quantity)
  const price = Number(draft.unitPrice)
  const perPack = Number(atomicPerPack)
  if (!Number.isFinite(qty) || !Number.isFinite(price) || !Number.isFinite(perPack) || perPack <= 0) {
    return fallback ?? ''
  }
  const totalAtomic = qty * perPack
  if (totalAtomic <= 0) return fallback ?? ''
  return (price / perPack).toFixed(6)
}

export function mergePatchResultIntoRow(
  row: PurchasingReviewQueueRow,
  draft: LineEditDraft,
  result: PurchasingPurchaseLineReviewResponse,
  derivedCostPerAtomic: string,
): PurchasingReviewQueueRow {
  const merged: PurchasingReviewQueueRow = {
    ...row,
    vendor_item_code: result.vendor_item_code ?? draft.vendorItemCode,
    description: draft.description,
    pack_text: draft.packText,
    quantity: result.quantity ?? draft.quantity,
    unit_price: result.unit_price ?? draft.unitPrice,
    extended_price: result.extended_price ?? draft.amount,
    cost_per_atomic_unit: result.cost_per_atomic_unit ?? derivedCostPerAtomic,
    review_status: result.review_status,
    review_flags: result.review_flags ?? row.review_flags,
    atomic_uom: result.atomic_uom ?? row.atomic_uom,
    atomic_per_pack: result.atomic_per_pack ?? row.atomic_per_pack,
  }
  return { ...merged, reasons: buildReasonsForQueueRow(merged) }
}

export type AutosaveDecision = 'skip' | 'apply' | 'stale_reschedule' | 'proceed'

export function decideAutosaveResult(args: {
  phase: 'before_save' | 'after_save'
  genAtStart: number
  currentGen: number
  baseline?: number | null
  ignoreEditGeneration?: boolean
  singleAttempt?: boolean
}): AutosaveDecision {
  const { phase, genAtStart, currentGen, baseline = null, ignoreEditGeneration = false, singleAttempt = false } =
    args

  if (phase === 'before_save') {
    if (!singleAttempt && !ignoreEditGeneration && baseline !== null && genAtStart === baseline) {
      return 'skip'
    }
    return 'proceed'
  }

  if (!ignoreEditGeneration && currentGen !== genAtStart) {
    return 'stale_reschedule'
  }
  return 'apply'
}

type PatchLineFn = typeof patchPurchasingPurchaseLine

export type UsePurchaseLineAutosaveOptions = {
  lineId: number
  row: PurchasingReviewQueueRow
  onSaved: (row: PurchasingReviewQueueRow) => void
  onClose: () => void
  modalRootRef: RefObject<HTMLElement | null>
  patchLine?: PatchLineFn
}

export function usePurchaseLineAutosave({
  lineId,
  row,
  onSaved,
  onClose,
  modalRootRef,
  patchLine = patchPurchasingPurchaseLine,
}: UsePurchaseLineAutosaveOptions) {
  const [draftsByLineId, setDraftsByLineId] = useState<Record<number, LineEditDraft>>({})
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())
  const editGenerationRef = useRef(0)
  const autosaveBaselineGenRef = useRef<number | null>(null)
  const suppressModalStateRef = useRef(false)
  const closeSaveEnqueuedRef = useRef(false)

  const previousLineIdRef = useRef(lineId)
  const leavingRowRef = useRef(row)
  const activeLineIdRef = useRef(lineId)
  const rowRef = useRef(row)
  const draftsByLineIdRef = useRef(draftsByLineId)
  const onSavedRef = useRef(onSaved)
  const onCloseRef = useRef(onClose)

  activeLineIdRef.current = lineId
  rowRef.current = row
  onSavedRef.current = onSaved
  onCloseRef.current = onClose

  const isEditGenerationDirty = useCallback(() => {
    const baseline = autosaveBaselineGenRef.current
    const current = editGenerationRef.current
    if (baseline === null) {
      return current !== 0
    }
    return current !== baseline
  }, [])

  const runLineSave = useCallback(
    async (
      targetLineId: number,
      targetRow: PurchasingReviewQueueRow,
      draft: LineEditDraft,
      singleAttempt: boolean,
      showSavingUi: boolean,
      ignoreEditGeneration: boolean,
      frozenEditGeneration?: number,
    ): Promise<void> => {
      const modalOk = () => !suppressModalStateRef.current

      let toggledSavingUi = false
      try {
        const genAtStart =
          singleAttempt && frozenEditGeneration !== undefined
            ? frozenEditGeneration
            : editGenerationRef.current

        const beforeDecision = decideAutosaveResult({
          phase: 'before_save',
          genAtStart,
          currentGen: editGenerationRef.current,
          baseline: autosaveBaselineGenRef.current,
          ignoreEditGeneration,
          singleAttempt,
        })
        if (beforeDecision === 'skip') {
          return
        }

        if (!lineEditDraftIsDirty(draft, targetRow)) {
          return
        }

        if (showSavingUi && modalOk()) {
          setSaving(true)
          toggledSavingUi = true
        }

        const result = await patchLine(targetLineId, buildPurchaseLinePatch(draft))
        const derivedCost = computeCostPerAtomicForDraft(
          draft,
          targetRow.atomic_per_pack,
          targetRow.cost_per_atomic_unit,
        )
        const updated = mergePatchResultIntoRow(targetRow, draft, result, derivedCost)

        onSavedRef.current(updated)

        if (activeLineIdRef.current !== targetLineId) {
          return
        }

        const afterDecision = decideAutosaveResult({
          phase: 'after_save',
          genAtStart,
          currentGen: editGenerationRef.current,
          ignoreEditGeneration,
        })

        if (afterDecision === 'stale_reschedule') {
          if (!singleAttempt && !saveTimeoutRef.current && modalOk()) {
            saveTimeoutRef.current = setTimeout(() => {
              saveTimeoutRef.current = null
              void performSaveRef.current()
            }, AUTOSAVE_DEBOUNCE_MS)
          }
          return
        }

        if (modalOk()) {
          setDraftsByLineId((prev) => {
            const next = { ...prev, [targetLineId]: lineEditDraftFromRow(updated) }
            draftsByLineIdRef.current = next
            return next
          })
          setLastSaved(new Date())
        }
        autosaveBaselineGenRef.current = genAtStart
      } catch (err) {
        if (modalOk()) {
          toast.error(formatApiError(err, 'Failed to save purchase line'))
        } else {
          console.error('Purchase line save failed after modal closed:', err)
        }
      } finally {
        if (toggledSavingUi) {
          setSaving(false)
        }
      }
    },
    [patchLine],
  )

  const performSaveRef = useRef<() => Promise<void>>(() => Promise.resolve())

  const performSave = useCallback((): Promise<void> => {
    const run = async (): Promise<void> => {
      const targetLineId = activeLineIdRef.current
      const targetRow = rowRef.current
      const draft =
        draftsByLineIdRef.current[targetLineId] ?? lineEditDraftFromRow(targetRow)
      await runLineSave(targetLineId, targetRow, draft, false, true, false)
    }

    const chained = saveChainRef.current.then(run, run)
    saveChainRef.current = chained.catch(() => {})
    return chained
  }, [runLineSave])

  performSaveRef.current = performSave

  const flushPendingSave = useCallback(async () => {
    const hadDebouncedSavePending = saveTimeoutRef.current !== null
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    await saveChainRef.current.catch(() => {})
    const generationDrifted = isEditGenerationDirty()
    if (hadDebouncedSavePending || generationDrifted) {
      await performSave()
    }
  }, [performSave, isEditGenerationDirty])

  const schedulePersistAfterCommit = useCallback(() => {
    editGenerationRef.current += 1
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null
      void performSave()
    }, AUTOSAVE_DEBOUNCE_MS)
  }, [performSave])

  const flushPersistAfterFieldCommit = useCallback(() => {
    const targetLineId = activeLineIdRef.current
    const targetRow = rowRef.current
    const draft =
      draftsByLineIdRef.current[targetLineId] ?? lineEditDraftFromRow(targetRow)
    if (!lineEditDraftIsDirty(draft, targetRow)) {
      return
    }
    editGenerationRef.current += 1
    window.setTimeout(() => {
      void flushPendingSave()
    }, 0)
  }, [flushPendingSave])

  const updateDraft = useCallback(
    (patch: Partial<LineEditDraft>) => {
      const current = draftsByLineIdRef.current[lineId] ?? lineEditDraftFromRow(row)
      const nextDraft = { ...current, ...patch }
      if (lineEditDraftsEqual(current, nextDraft)) {
        return
      }
      setDraftsByLineId((prev) => {
        const next = { ...prev, [lineId]: nextDraft }
        draftsByLineIdRef.current = next
        return next
      })
      if (!lineEditDraftsEqual(nextDraft, lineEditDraftFromRow(row))) {
        schedulePersistAfterCommit()
      }
    },
    [lineId, row, schedulePersistAfterCommit],
  )

  const enqueueBackgroundSave = useCallback(
    (ignoreEditGeneration: boolean, frozenEditGeneration?: number) => {
      const targetLineId = activeLineIdRef.current
      const targetRow = rowRef.current
      const draft =
        draftsByLineIdRef.current[targetLineId] ?? lineEditDraftFromRow(targetRow)

      const p = saveChainRef.current
        .catch(() => {})
        .then(() =>
          runLineSave(
            targetLineId,
            targetRow,
            draft,
            true,
            false,
            ignoreEditGeneration,
            frozenEditGeneration,
          ),
        )
      saveChainRef.current = p.catch(() => {})
      void p
    },
    [runLineSave],
  )

  const handleClose = useCallback(() => {
    const executeClose = () => {
      const hadDebouncedSavePending = saveTimeoutRef.current !== null
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }

      const generationDrifted = isEditGenerationDirty()
      const needsPersist = hadDebouncedSavePending || generationDrifted

      suppressModalStateRef.current = true

      if (needsPersist && !closeSaveEnqueuedRef.current) {
        closeSaveEnqueuedRef.current = true
        enqueueBackgroundSave(true)
      }

      onCloseRef.current()
    }

    const ae = globalThis.document?.activeElement ?? null
    if (ae instanceof HTMLElement && modalRootRef.current?.contains(ae)) {
      ae.blur()
      window.setTimeout(executeClose, 0)
      return
    }
    executeClose()
  }, [enqueueBackgroundSave, isEditGenerationDirty, modalRootRef])

  useEffect(() => {
    setDraftsByLineId((prev) => {
      if (prev[lineId]) return prev
      const next = { ...prev, [lineId]: lineEditDraftFromRow(row) }
      draftsByLineIdRef.current = next
      return next
    })
  }, [lineId, row])

  useEffect(() => {
    if (autosaveBaselineGenRef.current === null) {
      autosaveBaselineGenRef.current = editGenerationRef.current
    }
  }, [])

  useLayoutEffect(() => {
    if (lineId === previousLineIdRef.current) {
      leavingRowRef.current = row
      return
    }

    const leavingId = previousLineIdRef.current
    const leavingRow = leavingRowRef.current
    const leavingDraft =
      draftsByLineIdRef.current[leavingId] ?? lineEditDraftFromRow(leavingRow)
    const leavingEditGenerationSnapshot = editGenerationRef.current

    previousLineIdRef.current = lineId
    leavingRowRef.current = row

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
      saveTimeoutRef.current = null
    }

    editGenerationRef.current = 0
    autosaveBaselineGenRef.current = 0

    const p = saveChainRef.current
      .catch(() => {})
      .then(() =>
        runLineSave(
          leavingId,
          leavingRow,
          leavingDraft,
          true,
          false,
          false,
          leavingEditGenerationSnapshot,
        ),
      )
    saveChainRef.current = p.catch(() => {})
  }, [lineId, row, runLineSave])

  useEffect(() => {
    draftsByLineIdRef.current = draftsByLineId
  }, [draftsByLineId])

  useEffect(() => {
    return () => {
      if (suppressModalStateRef.current || closeSaveEnqueuedRef.current) {
        return
      }

      const hadDebouncedSavePending = saveTimeoutRef.current !== null
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = null
      }

      const generationDrifted = isEditGenerationDirty()
      if (!hadDebouncedSavePending && !generationDrifted) {
        return
      }

      suppressModalStateRef.current = true
      closeSaveEnqueuedRef.current = true
      const frozenGen = editGenerationRef.current
      enqueueBackgroundSave(true, frozenGen)
    }
  }, [enqueueBackgroundSave, isEditGenerationDirty])

  const draft = draftsByLineId[lineId] ?? lineEditDraftFromRow(row)

  return {
    draft,
    updateDraft,
    flushPersistAfterFieldCommit,
    handleClose,
    saving,
    lastSaved,
  }
}
